import { Fragment } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import { TRIAL_DAYS } from '@/config';
import { useMediaQuery } from '@/hooks';
import { useTrialDiscount } from '@/hooks/user';
import Redeem from '@/icons/redeem.svg';
import routes from '@/routes';
import { setPrice } from '@/store/checkout';
import { AUTH_MODAL_TYPES, openAuthModal } from '@/store/profile';
import { cn } from '@/utils';
import { formatPrice } from '@/utils/money';

function PlanCard({ plansData = {}, withCTA = true, large = false }) {
	const { t, i18n } = useTranslation();
	const mdDown = useMediaQuery('mdDown');
	const navigate = useNavigate();
	const trialDiscount = useTrialDiscount();
	const loggedIn = useSelector(state => state.profile.loggedIn);
	const dispatch = useDispatch();

	const { plans, trialPlan, monthlyPlan } = plansData;

	const planTitlesMapping = [t('common.today'), t('common.then')];

	const trialDiscountFlag = (
		<div className="inline-flex min-h-8 items-center justify-center gap-2 rounded-lg bg-red-background px-2 py-1 text-red md:w-fit">
			<Redeem />
			<span className="justify-center text-xs font-semibold lg:text-sm">
				{t('billing.trialDiscount')}
			</span>
		</div>
	);

	return (
		<div
			className={cn(
				'w-full rounded border border-grey-200 bg-white',
				!large && 'max-w-[606px]'
			)}
		>
			<div
				className={cn(
					'flex flex-col items-center gap-6 p-4 pt-6 lg:p-6 lg:pt-8',
					!withCTA && 'pb-3 lg:pb-4'
				)}
			>
				<div className="flex flex-col items-center gap-3">
					{trialDiscount && mdDown && trialDiscountFlag}
					<div className="flex items-center justify-center">
						{plans.map((plan, idx) => {
							const isTrialDiscountVisible =
								plan.handlerId === trialPlan.handlerId && trialDiscount;

							const textPrice = formatPrice({
								amount: isTrialDiscountVisible ? 0 : plan.baseAmount,
								currency: plan.currency
							});

							return (
								<Fragment key={plan.handlerId}>
									{idx !== 0 && (
										<hr
											className={cn(
												'mx-2.5 h-[72px] w-0 border-l border-grey-200',
												large && 'md:h-[104px] lg:mx-[60px]'
											)}
										/>
									)}
									<div className="flex min-w-[124px] flex-1 flex-auto flex-col items-center lg:min-w-[186px] lg:gap-2">
										<p className="text-xs font-semibold lg:text-base">
											{planTitlesMapping[idx] || plan.name}
										</p>
										<span
											className={cn(
												'font-title text-xl lg:text-[32px] lg:leading-10',
												isTrialDiscountVisible && 'text-red'
											)}
										>
											{textPrice}
										</span>
										<span className="text-2xs text-grey-500 lg:text-xs lg:font-semibold">
											{i18n.exists(`plans.${plan.name}.shortName`)
												? t(`plans.${plan.name}.shortName`)
												: t(`plans.${plan.name}.name`)}
										</span>

										{isTrialDiscountVisible &&
											!mdDown &&
											trialDiscountFlag}
									</div>
								</Fragment>
							);
						})}
					</div>
				</div>
				<div
					className={cn(
						'rounded-lg bg-grey-100 p-3',
						large ? 'lg:py-5' : 'lg:p-5'
					)}
				>
					<p className="whitespace-pre-line text-center text-xs text-grey-500 lg:text-sm">
						<Trans
							i18nKey="plans.description"
							components={{
								strong: <strong className="font-semibold" />
							}}
							values={{
								trialPrice: formatPrice({
									amount: trialDiscount ? 0 : trialPlan?.baseAmount,
									currency: trialPlan?.currency
								}),
								monthlyPrice: formatPrice({
									amount: monthlyPlan?.baseAmount,
									currency: monthlyPlan?.currency
								}),
								trialDays: TRIAL_DAYS
							}}
						/>
					</p>
				</div>
			</div>
			<div
				className={cn(
					'flex flex-col items-center justify-center gap-2.5 p-4 lg:px-6',
					withCTA ? 'border-t border-grey-200' : 'pt-0 lg:pb-6'
				)}
			>
				{withCTA && (
					<Button
						size={mdDown ? 'xs' : 'default'}
						onClick={() => {
							dispatch(setPrice({ id: trialPlan.handlerId }));

							if (loggedIn) {
								return navigate(routes.checkout);
							}

							dispatch(
								openAuthModal({
									type: AUTH_MODAL_TYPES.SIGN_UP,
									redirect: routes.checkout
								})
							);
						}}
					>
						{t('billing.button.startTrial', { trialDays: TRIAL_DAYS })}
					</Button>
				)}
				<p className="text-center text-xs leading-5 text-grey-500">
					{t('plans.trial.disclaimer')}
				</p>
			</div>
		</div>
	);
}

export default PlanCard;
